import { ReactElement, useCallback, useEffect, useId, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  useFieldArray,
  useForm,
  UseFormSetValue,
  Control,
  FieldErrors,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import cn from "classnames";

import {
  FormTextInput,
  FormField,
  FormSelect,
} from "oneclick-component/src/components/forms";
import { Button, ButtonLink } from "oneclick-component/src/components/Button";
import {
  BriefStation,
  useCreateUserHandlerUsersPostMutation as useCreateUser,
  useListRailwayLineHandlerRailwayLinesGetQuery as useListRailwayLine,
} from "oneclick-component/src/store/apis/enhancedApi";
import useShowMessage from "oneclick-component/src/hooks/useShowMessage";
import { Option } from "oneclick-component/src/components/inputs";
import AppRoutes from "../../routes/AppRoutes";
import { LazyLoadStationSelectionDropdown } from "../../components/StationSelectionDropdown";
import { useShowError } from "../../hooks/useShowError";
import { ALL_ADMIN_ROLES } from "../../constants/adminRoles";
import {
  CreateLandlorfUserFormValues,
  CreateManagerUserFormValues,
  createUserFormSchema,
  CreateUserFormStationRole,
  CreateUserFormValues,
} from "./createUserForm";

interface StationRoleFieldsProps {
  index: number;
  setValue: UseFormSetValue<CreateUserFormValues>;
  control: Control<CreateUserFormValues>;
  stationRolesFieldErrors: FieldErrors<CreateManagerUserFormValues>["stationRoles"];
  removeStationRole: (index: number) => void;
}

const StationRoleFields = (props: StationRoleFieldsProps): ReactElement => {
  const {
    index,
    setValue,
    control,
    stationRolesFieldErrors,
    removeStationRole,
  } = props;
  const { t } = useTranslation();

  const onStationSelected = useCallback(
    (station: BriefStation | null) => {
      if (station != null) {
        setValue(`stationRoles.${index}.stationId`, station.id);
      }
    },
    [setValue, index]
  );

  const onRemoveStationRole = useCallback(() => {
    removeStationRole(index);
  }, [removeStationRole, index]);

  return (
    <div className={cn("flex", "gap-3", "items-end", "mb-3")}>
      <FormField.Control
        name={`stationRoles.${index}.stationId`}
        control={control}
      >
        {({ field }) => (
          <FormField.Container
            label={t("user.create.form.stationRole.stationId.label")}
            errorMessage={stationRolesFieldErrors?.[index]?.role?.message}
            required={true}
          >
            <LazyLoadStationSelectionDropdown
              selectedStationId={field.value}
              onStationSelected={onStationSelected}
              onChange={field.onChange}
            />
          </FormField.Container>
        )}
      </FormField.Control>
      <Button onClick={onRemoveStationRole} className="h-10" theme="red">
        <Trans i18nKey="user.create.form.stationRoles.remove" />
      </Button>
    </div>
  );
};

const CreateUserScreen = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createUser, { isLoading: isCreatingUser }] = useCreateUser();
  const {
    data: railwayLineData,
    isLoading: isLoadingRailwayLines,
    error: listRailwayLineError,
  } = useListRailwayLine();
  const { showError } = useShowError();
  const showMessage = useShowMessage();

  useEffect(() => {
    if (listRailwayLineError != null) {
      showError(listRailwayLineError);
    }
  }, [listRailwayLineError, showError]);

  const formId = useId();
  const {
    setValue,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<CreateUserFormValues>({
    resolver: zodResolver(createUserFormSchema),
    defaultValues: {
      nameEn: "",
      nameZhHk: "",
      adminRole: "MANAGER",
      email: "",
      stationRoles: [],
    },
  });

  const adminRole = watch("adminRole");
  useEffect(() => {
    setValue("stationRoles", []);
    setValue("lineCode", "");
  }, [adminRole, setValue]);

  const adminRoleOptions: Option<string>[] = useMemo(() => {
    return ALL_ADMIN_ROLES.map((role) => ({
      name: role,
      value: role,
    }));
  }, []);

  const {
    fields: stationRoleFields,
    append: appendStationRole,
    remove: removeStationRole,
  } = useFieldArray({
    control,
    name: "stationRoles",
  });

  const onAddStationRole = useCallback(() => {
    appendStationRole({
      // workaround initial value must match schema
      stationId: null as unknown as number,
      role: "ADMIN",
    });
  }, [appendStationRole]);

  const lineCodeOptions = useMemo(() => {
    if (railwayLineData == null) {
      return [];
    }
    return railwayLineData.railwayLines.map((rl) => ({
      name: rl.shortCode,
      value: rl.shortCode,
    }));
  }, [railwayLineData]);

  const onValidSubmit = useCallback(
    async (data: CreateUserFormValues) => {
      let lineCode: string | undefined;
      let stationRoles: CreateUserFormStationRole[] | undefined;
      if (data.adminRole === "LANDLORD") {
        if (data.lineCode.length > 0) {
          lineCode = data.lineCode;
        }
      } else if (data.adminRole === "MANAGER") {
        stationRoles = data.stationRoles;
      }
      try {
        await createUser({
          createUserRequest: {
            nameEn: data.nameEn,
            nameZhHk: data.nameZhHk,
            adminRole: data.adminRole,
            phoneNumber: "", // deprecated
            email: data.email,
            stationRoles: stationRoles,
            lineCode: lineCode,
          },
        }).unwrap();
        showMessage({
          type: "success",
          title: t("user.create.successDialog.title"),
          message: t("user.create.successDialog.message"),
        });
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigate(AppRoutes.UserListScreen.render());
      } catch (err: unknown) {
        console.error(err);
        showError(err);
      }
    },
    [createUser, showMessage, showError, t, navigate]
  );

  const { stationRolesFieldErrors, lineCodeFieldErrors } = useMemo(() => {
    return {
      stationRolesFieldErrors: (
        errors as FieldErrors<CreateManagerUserFormValues>
      ).stationRoles,
      lineCodeFieldErrors: (errors as FieldErrors<CreateLandlorfUserFormValues>)
        .lineCode,
    };
  }, [errors]);

  return (
    <main className={cn("px-4", "pb-20", "md:px-0")}>
      <form
        id={formId}
        className={cn("flex", "flex-col", "gap-y-5")}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(onValidSubmit)}
      >
        <FormTextInput
          name={"nameEn"}
          control={control}
          label={t("user.create.form.nameEn.label")}
          required={true}
          errorMessage={errors.nameEn?.message}
          placeholder={t("user.create.form.nameEn.placeholder")}
        />
        <FormTextInput
          name={"nameZhHk"}
          control={control}
          label={t("user.create.form.nameZhHk.label")}
          required={true}
          errorMessage={errors.nameZhHk?.message}
          placeholder={t("user.create.form.nameZhHk.placeholder")}
        />
        <FormSelect
          name={"adminRole"}
          control={control}
          options={adminRoleOptions}
          label={t("user.create.form.adminRole.label")}
          required={true}
        />
        <FormTextInput
          name={"email"}
          control={control}
          label={t("user.create.form.email.label")}
          required={true}
          errorMessage={errors.email?.message}
          placeholder={t("user.create.form.email.placeholder")}
        />
        {adminRole === "MANAGER" ? (
          <div>
            <label
              className={cn(
                "block",
                "text-base",
                "font-bold",
                "leading-5",
                "text-left",
                "mb-4"
              )}
            >
              {t("user.create.form.stationRoles.label")}
              {"*"}
            </label>
            {stationRoleFields.map((field, index) => (
              <StationRoleFields
                key={`StationRoleFields-${field.stationId}-${field.role}`}
                index={index}
                setValue={setValue}
                control={control}
                stationRolesFieldErrors={stationRolesFieldErrors}
                removeStationRole={removeStationRole}
              />
            ))}
            <Button theme="primary" onClick={onAddStationRole}>
              <Trans i18nKey="user.create.form.stationRoles.add" />
            </Button>
            {stationRolesFieldErrors?.message != null ? (
              <p className={cn("mt-2", "text-sm", "text-red-600", "text-left")}>
                {stationRolesFieldErrors.message}
              </p>
            ) : null}
          </div>
        ) : null}
        {adminRole === "LANDLORD" ? (
          <FormSelect
            name={"lineCode"}
            control={control}
            options={lineCodeOptions}
            label={t("user.create.form.lineCode.label")}
            required={true}
            errorMessage={lineCodeFieldErrors?.message}
            placeholder={t("user.create.form.lineCode.placeholder")}
            disabled={isLoadingRailwayLines}
          />
        ) : null}
      </form>
      <footer
        className={cn(
          "fixed",
          "bottom-0",
          "left-0",
          "bg-white",
          "w-full",
          "h-16",
          "flex",
          "justify-end",
          "px-6",
          "py-3"
        )}
      >
        <ButtonLink
          theme={"white"}
          className={"mr-3"}
          to={AppRoutes.UserListScreen.render()}
        >
          <Trans i18nKey="user.create.footer.button.cancel" />
        </ButtonLink>
        <Button type="submit" form={formId} disabled={isCreatingUser}>
          <Trans i18nKey="user.create.footer.button.create" />
        </Button>
      </footer>
    </main>
  );
};

export default CreateUserScreen;
